// RITA-1549 — „Drucken“, „Erneut drucken“ und „Ansehen“ der Vertragserstellung: die PDF in einem neuen Tab.
//
// Ablauf wie bei der PDF-Probe (features/templates/hooks/usePdfProbe.ts): Der neue Tab wird noch im Klick geöffnet
// (sonst blockiert der Browser das Fenster) und zeigt einen Wartetext; dann läuft die Mutation (Drucken bzw. Erneut
// drucken) oder gleich der Dateiabruf (Ansehen); die PDF kommt als Blob-Adresse in den Tab. Jeder nicht erfolgreiche
// Ausgang schließt den Tab – auch „Vorlage geändert“ und ein Entwurfskonflikt, die das Fenster bzw. die Seite mit
// eigener Rückfrage beantworten (Befund der Plankritik). Scheitert nach erfolgreicher Ablage nur noch der Dateiabruf,
// ist die PDF trotzdem erzeugt, abgelegt und protokolliert: Tab zu, Hinweis, `onGedruckt` trotzdem, keine zweite
// Mutation. Nach einer erfolgreichen Mutation lädt Apollo die Liste „Dokumente“ des Beraters neu; die dafür nötige
// Berater-UUID steht erst in der Antwort, deshalb `refetchQueries` in Funktionsform.
// RITA-1552 (E6, Befund F1 aus dem Review von MR 1): `drucken` verlangt den Vertragsbezug ausdrücklich und schickt ihn
// immer als `expected_contract_entry_id` mit – bei der Rücksendung die Kennung des „Gehört zu“-Vertrags oder null (kein
// Vertrag aus dem PRM), beim Vertrag null (der Server wertet ihn dort nicht aus). Ist inzwischen ein neuer Vertrag
// gedruckt, meldet der Server CONTRACT_CHANGED; der Nutzertext kommt wie bei allen Codes aus fehlertextFuerCode.
import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import type { ContractCreationKind, ContractCreationPrintInput } from 'global-intern-types';
import getAllAgentFilesQuery from '../../../Apollo/queries/getAllAgentFiles';
import { PDF_PROBE_WARTETEXT } from '../../templates/hooks/usePdfProbe';
import { graphqlFehlerCode, graphqlFehlerErweiterungen } from '../../templates/services/vertragsvorlageFehler';
import { vertragsdateiAbrufen, VERTRAGSDATEI_FEHLER, VertragsdateiFehler } from '../api/vertragsdatei';
import {
  Druckantwort,
  DruckMutationAntwort,
  DruckVariablen,
  ErneutDruckenVariablen,
  ErneutMutationAntwort,
  PRINT_VERTRAG,
  REPRINT_VERTRAG,
} from '../api/vertragsdruck';
import {
  ABGELEGT_NICHT_GEOEFFNET,
  DRUCKFEHLER,
  fehlertextFuerCode,
  TAB_BLOCKIERT,
  WIRD_GEDRUCKT,
} from '../services/vertragsdruckTexte';
import { alsArt } from '../services/vertragserstellungTexte';

/** Fremde Speicherung, die der Druck mit `expected_revision` getroffen hat (CONFLICT); `revision` leer = unbekannt. */
export interface Druckkonflikt {
  updated_at: string;
  updated_by_name: string | null;
  revision: string;
}

export type Druckergebnis =
  /** Gedruckt und abgelegt; `nichtGeoeffnet`: nur der Dateiabruf scheiterte, der Tab ist zu (Hinweis in `fehler`). */
  | { art: 'ok'; antwort: Druckantwort; nichtGeoeffnet?: true }
  /** TEMPLATE_CHANGED (Herkunft template): nichts gedruckt; das Fenster zeigt Hinweis b) mit „Vorschau neu laden“. */
  | { art: 'vorlageGeaendert'; kind: ContractCreationKind; name: string; status: string }
  /** CONFLICT: der Entwurf wurde inzwischen gespeichert; nichts gedruckt. */
  | { art: 'konflikt'; konflikt: Druckkonflikt }
  /** DISCARDED: der Entwurf wurde inzwischen verworfen; nichts gedruckt. */
  | { art: 'verworfen' }
  /**
   * Nutzertext steht auch in `fehler`. `code`: Fehlercode des Servers (fehlt bei Fehlern ohne Code, etwa Netzwerk,
   * gesperrtem Tab oder ungültiger Antwort). RITA-1552: Wer auf einen Fehler reagiert (CONTRACT_CHANGED), prüft den
   * Code, nicht den Nutzertext.
   */
  | { art: 'fehler'; text: string; code?: string };

export interface VertragDruckenAbhaengigkeiten {
  fensterOeffnen?: () => Window | null;
  abrufen?: typeof vertragsdateiAbrufen;
}

export interface VertragDruckenOptionen {
  agentId: number;
  /** Nach jeder erfolgreichen Mutation (Drucken, Erneut drucken) – auch wenn der Dateiabruf danach scheitert. */
  onGedruckt?: (antwort: Druckantwort) => void;
  /** Nach Fehlern, die den Protokollstand betreffen (FILE_DELETED, SUPERSEDED, DRUCKFEHLER, Datei beim Ansehen weg). */
  onProtokollNeuLaden?: () => void;
  abhaengigkeiten?: VertragDruckenAbhaengigkeiten;
}

export interface VertragDrucken {
  /**
   * `expectedContractEntryId` (RITA-1552, E6): bei der Rücksendung der Druck mit Vertrag, zu dem sie gehört, oder null
   * (kein Vertrag aus dem PRM); beim Vertrag null. Bewusst ohne Vorgabewert, damit kein Druckweg ihn vergisst.
   */
  drucken: (
    eingabe: ContractCreationPrintInput,
    expectedRevision: string | null,
    expectedContractEntryId: number | null,
  ) => Promise<Druckergebnis>;
  erneutDrucken: (entryId: number) => Promise<Druckergebnis>;
  ansehen: (filename: string) => Promise<void>;
  laeuft: boolean;
  fehler: string;
  fehlerSchliessen: () => void;
}

/** Liste „Dokumente“ des Beraters neu laden (anonyme Legacy-Abfrage mit der Berater-UUID als `agent_id`). */
export const dokumenteNeuLaden = (
  agentUuid: string | undefined,
): Array<{ query: typeof getAllAgentFilesQuery; variables: { agent_id: string } }> =>
  agentUuid ? [{ query: getAllAgentFilesQuery, variables: { agent_id: agentUuid } }] : [];

function alsObjekt(wert: unknown): Record<string, unknown> | undefined {
  return typeof wert === 'object' && wert !== null ? (wert as Record<string, unknown>) : undefined;
}

/** Meldung des ersten GraphQL-Fehlers (für PAGE_SETTINGS_DIFFER wird sie durchgereicht). */
function graphqlFehlerMeldung(fehler: unknown): string | null {
  const liste = alsObjekt(fehler)?.graphQLErrors;
  const erster = Array.isArray(liste) ? alsObjekt(liste[0]) : undefined;
  return typeof erster?.message === 'string' ? erster.message : null;
}

const alsText = (wert: unknown, sonst: string): string => (typeof wert === 'string' ? wert : sonst);

/** Ein Schritt im geöffneten Tab: liefert das Ergebnis und ob die PDF im Tab geladen wurde (sonst Tab zu). */
type Schritt<T> = (tab: Window) => Promise<{ ergebnis: T; pdfGeladen: boolean }>;

export default function useVertragDrucken({
  agentId,
  onGedruckt,
  onProtokollNeuLaden,
  abhaengigkeiten = {},
}: VertragDruckenOptionen): VertragDrucken {
  const [drucke] = useMutation<DruckMutationAntwort, DruckVariablen>(PRINT_VERTRAG, {
    refetchQueries: ({ data }) => dokumenteNeuLaden(data?.printContractCreation?.agent_uuid),
  });
  const [druckeErneut] = useMutation<ErneutMutationAntwort, ErneutDruckenVariablen>(REPRINT_VERTRAG, {
    refetchQueries: ({ data }) => dokumenteNeuLaden(data?.reprintContractCreation?.agent_uuid),
  });
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState('');
  const laeuftRef = useRef(false);
  const aktivRef = useRef(true);
  const abrufen = abhaengigkeiten.abrufen ?? vertragsdateiAbrufen;
  const fensterOeffnen = abhaengigkeiten.fensterOeffnen ?? (() => window.open('', '_blank'));

  useEffect(() => {
    aktivRef.current = true;
    return () => {
      aktivRef.current = false;
    };
  }, []);

  /** Nutzertext anzeigen, sofern der Aufrufer noch eingehängt ist (Antworten nach dem Abbau verwerfen). */
  const meldung = (text: string) => {
    if (aktivRef.current) setFehler(text);
  };

  /** Sperre gegen Doppelklick, Tab synchron im Klick, Wartetext, Schritt, Tab zu bei jedem Ausgang ohne PDF. */
  async function ausfuehren<T>(schritt: Schritt<T>, beschaeftigt: T, blockiert: T): Promise<T> {
    if (laeuftRef.current) return beschaeftigt;
    const tab = fensterOeffnen();
    if (!tab) {
      setFehler(TAB_BLOCKIERT);
      return blockiert;
    }
    try {
      tab.document.title = PDF_PROBE_WARTETEXT;
      if (tab.document.body) tab.document.body.textContent = PDF_PROBE_WARTETEXT;
    } catch (error) {
      // Ein fremdes oder bereits weitergeleitetes Fenster lässt sich nicht beschreiben; die PDF kommt trotzdem.
    }
    laeuftRef.current = true;
    setLaeuft(true);
    setFehler('');
    let pdfGeladen = false;
    try {
      const ablauf = await schritt(tab);
      pdfGeladen = ablauf.pdfGeladen;
      return ablauf.ergebnis;
    } finally {
      // Das Schließen darf die Freigabe der Sperre nie verhindern (Nachprüfung MR 4).
      try {
        if (!pdfGeladen) tab.close();
      } catch (error) {
        // Ein fremdes oder schon geschlossenes Fenster lässt sich nicht schließen; die Sperre wird trotzdem gelöst.
      }
      laeuftRef.current = false;
      if (aktivRef.current) setLaeuft(false);
    }
  }

  /** Die abgelegte PDF in den Tab laden; die Blob-Adresse bleibt gültig, solange diese Seite offen ist (kein revoke). */
  const pdfInDenTab = async (tab: Window, filename: string): Promise<void> => {
    const blob = await abrufen(filename);
    // eslint-disable-next-line no-param-reassign -- der Tab ist das Ziel, kein Eingabewert
    tab.location.href = URL.createObjectURL(blob);
  };

  /** Nach erfolgreicher Mutation: PDF öffnen; scheitert nur noch der Abruf, bleibt der Druck gültig (Hinweis). */
  const nachAblage = async (
    tab: Window,
    antwort: Druckantwort,
  ): Promise<{ ergebnis: Druckergebnis; pdfGeladen: boolean }> => {
    let ablauf: { ergebnis: Druckergebnis; pdfGeladen: boolean };
    try {
      await pdfInDenTab(tab, antwort.filename);
      ablauf = { ergebnis: { art: 'ok', antwort }, pdfGeladen: true };
    } catch (error) {
      meldung(ABGELEGT_NICHT_GEOEFFNET);
      ablauf = { ergebnis: { art: 'ok', antwort, nichtGeoeffnet: true }, pdfGeladen: false };
    }
    // Genau einmal, außerhalb des Abruf-try und selbst abgesichert (KI-Review MR 3): Wirft der Aufrufer, zählt das
    // weder als Dateiabruf-Fehler noch darf es den Druck als Fehlschlag enden lassen (Tab schließen, Promise ablehnen).
    try {
      onGedruckt?.(antwort);
    } catch (error) {
      // Der Druck ist verbucht und die PDF ggf. offen; ein Fehler in der Karte ändert daran nichts.
    }
    return ablauf;
  };

  /** Fehler der Mutation: Rückfragen (TEMPLATE_CHANGED, CONFLICT, DISCARDED) oder Nutzertext je Code. */
  const mutationsfehler = (error: unknown): Druckergebnis => {
    const code = graphqlFehlerCode(error);
    const erweiterungen = graphqlFehlerErweiterungen(error);
    if (code === 'TEMPLATE_CHANGED') {
      return {
        art: 'vorlageGeaendert',
        kind: alsArt(erweiterungen.kind) ?? 'contract',
        name: alsText(erweiterungen.template_name, ''),
        status: alsText(erweiterungen.template_status, 'changed'),
      };
    }
    if (code === 'CONFLICT') {
      return {
        art: 'konflikt',
        konflikt: {
          updated_at: alsText(erweiterungen.updated_at, new Date().toISOString()),
          updated_by_name: typeof erweiterungen.updated_by_name === 'string' ? erweiterungen.updated_by_name : null,
          revision: alsText(erweiterungen.revision, ''),
        },
      };
    }
    if (code === 'DISCARDED') return { art: 'verworfen' };
    const text = fehlertextFuerCode(code, graphqlFehlerMeldung(error), alsArt(erweiterungen.kind));
    meldung(text);
    // Auch nach einem allgemeinen Fehler neu laden: Eine verloren gegangene, aber erfolgreiche Antwort bliebe sonst
    // unsichtbar, und ein zweiter Klick druckte doppelt (Plankritik).
    onProtokollNeuLaden?.();
    return code ? { art: 'fehler', text, code } : { art: 'fehler', text };
  };

  const ungueltigeAntwort = (): Druckergebnis => {
    meldung(DRUCKFEHLER);
    onProtokollNeuLaden?.();
    return { art: 'fehler', text: DRUCKFEHLER };
  };

  const nichtJetzt: Druckergebnis = { art: 'fehler', text: WIRD_GEDRUCKT };
  const tabBlockiert: Druckergebnis = { art: 'fehler', text: TAB_BLOCKIERT };

  const drucken = (
    eingabe: ContractCreationPrintInput,
    expectedRevision: string | null,
    expectedContractEntryId: number | null,
  ) =>
    ausfuehren<Druckergebnis>(
      async (tab) => {
        let antwort: Druckantwort | undefined;
        try {
          const { data } = await drucke({
            variables: {
              agent_id: agentId,
              input: eingabe,
              expected_revision: expectedRevision,
              expected_contract_entry_id: expectedContractEntryId,
            },
          });
          antwort = data?.printContractCreation;
        } catch (error) {
          return { ergebnis: mutationsfehler(error), pdfGeladen: false };
        }
        if (!antwort || !antwort.filename) return { ergebnis: ungueltigeAntwort(), pdfGeladen: false };
        return nachAblage(tab, antwort);
      },
      nichtJetzt,
      tabBlockiert,
    );

  const erneutDrucken = (entryId: number) =>
    ausfuehren<Druckergebnis>(
      async (tab) => {
        let antwort: Druckantwort | undefined;
        try {
          const { data } = await druckeErneut({ variables: { agent_id: agentId, entry_id: entryId } });
          antwort = data?.reprintContractCreation;
        } catch (error) {
          return { ergebnis: mutationsfehler(error), pdfGeladen: false };
        }
        if (!antwort || !antwort.filename) return { ergebnis: ungueltigeAntwort(), pdfGeladen: false };
        return nachAblage(tab, antwort);
      },
      nichtJetzt,
      tabBlockiert,
    );

  const ansehen = (filename: string) =>
    ausfuehren<void>(
      async (tab) => {
        try {
          await pdfInDenTab(tab, filename);
          return { ergebnis: undefined, pdfGeladen: true };
        } catch (error) {
          const dateiFehler = error instanceof VertragsdateiFehler ? error : null;
          meldung(dateiFehler ? dateiFehler.message : VERTRAGSDATEI_FEHLER.allgemein);
          // Die Datei wurde unter „Dokumente“ gelöscht: Die Zeile zeigt es erst nach dem Neuladen.
          if (dateiFehler?.code === 'geloescht') onProtokollNeuLaden?.();
          return { ergebnis: undefined, pdfGeladen: false };
        }
      },
      undefined,
      undefined,
    );

  return { drucken, erneutDrucken, ansehen, laeuft, fehler, fehlerSchliessen: () => setFehler('') };
}
